/* ==========================================================================
   Manuals & Datasheets Page Component (100% Localized Multilingual)
   Provides organized access to official customer PDFs, Edge/Centralized manuals,
   driver specifications, datasheets, and design handbooks.
   ========================================================================== */

import { t, getLocale } from '../locales/i18n.js';
import { getSvgIcon } from '../components/Icons.js';

const manualsUi = {
  cs: {
    eyebrow: 'DOKUMENTACE A MATERIÁLY',
    title: 'Manuály a technické listy',
    desc: 'Získejte oficiální technickou dokumentaci, manuály pro Edge integrace (ACAP/HEOP), příručky pro centrální servery, specifikace ovladačů REST a ONVIF a certifikované technické listy pro systémy akustické detekce JALUD Embedded.',
    allDocs: 'Všechny dokumenty',
    filterEdge: '⚡ Edge řešení',
    filterCentralized: '🏢 Centralizovaná řešení',
    filterApps: '💻 Aplikace a nástroje',
    filterIntegration: '🔌 Ovladače a API',
    filterDesign: '📐 Instalační příručky',
    filterDatasheets: '📄 Technické listy a certifikace',
    downloadPdf: 'Stáhnout PDF ↓',
    enDocBadge: 'Dokument v angličtině'
  },
  en: {
    eyebrow: 'DOCUMENTATION & RESOURCES',
    title: 'Manuals & Datasheets',
    desc: 'Access official technical documentation, Edge (ACAP/HEOP) manuals, Centralized Server guides, REST & ONVIF driver specifications, and certified datasheets for JALUD Embedded acoustic detection systems.',
    allDocs: 'All Documents',
    filterEdge: '⚡ Edge Solutions',
    filterCentralized: '🏢 Centralized Solutions',
    filterApps: '💻 Applications & Tools',
    filterIntegration: '🔌 Drivers & APIs',
    filterDesign: '📐 Installation & Guides',
    filterDatasheets: '📄 Datasheets & Certificates',
    downloadPdf: 'Download PDF ↓',
    enDocBadge: 'English Document'
  },
  de: {
    eyebrow: 'DOKUMENTATION & RESSOURCEN',
    title: 'Handbücher & Datenblätter',
    desc: 'Zugriff auf offizielle technische Dokumentation, Edge-Handbücher, Server-Leitfäden und Datenblätter.',
    allDocs: 'Alle Dokumente',
    filterEdge: '⚡ Edge-Lösungen',
    filterCentralized: '🏢 Server-Lösungen',
    filterApps: '💻 Anwendungen',
    filterIntegration: '🔌 Treiber & APIs',
    filterDesign: '📐 Installationsanleitungen',
    filterDatasheets: '📄 Datenblätter & Zertifikate',
    downloadPdf: 'PDF herunterladen ↓',
    enDocBadge: 'Englisches Dokument'
  },
  es: {
    eyebrow: 'DOCUMENTACIÓN Y RECURSOS',
    title: 'Manuales y Fichas Técnicas',
    desc: 'Acceda a la documentación técnica oficial, manuales de Edge y servidores, controladores y hojas de especificaciones.',
    allDocs: 'Todos los documentos',
    filterEdge: '⚡ Soluciones Edge',
    filterCentralized: '🏢 Soluciones Centralizadas',
    filterApps: '💻 Aplicaciones',
    filterIntegration: '🔌 Controladores y APIs',
    filterDesign: '📐 Guías de Instalación',
    filterDatasheets: '📄 Fichas Técnicas y CE',
    downloadPdf: 'Descargar PDF ↓',
    enDocBadge: 'Documento en inglés'
  },
  it: {
    eyebrow: 'DOCUMENTAZIONE E RISORSE',
    title: 'Manuali e Schede Tecniche',
    desc: 'Accedi alla documentazione tecnica ufficiale, manuali di Edge e server, driver e schede di specifiche.',
    allDocs: 'Tutti i documenti',
    filterEdge: '⚡ Soluzioni Edge',
    filterCentralized: '🏢 Soluzioni Centralizzate',
    filterApps: '💻 Applicazioni',
    filterIntegration: '🔌 Driver e API',
    filterDesign: '📐 Guide di Installazione',
    filterDatasheets: '📄 Schede Tecniche e CE',
    downloadPdf: 'Scarica PDF ↓',
    enDocBadge: 'Documento in inglese'
  },
  pt: {
    eyebrow: 'DOCUMENTAÇÃO E RECURSOS',
    title: 'Manuais e Fichas Técnicas',
    desc: 'Aceda à documentação técnica oficial, manuais de Edge e servidores, controladores e folhas de especificações.',
    allDocs: 'Todos os documentos',
    filterEdge: '⚡ Soluções Edge',
    filterCentralized: '🏢 Soluções Centralizadas',
    filterApps: '💻 Aplicações',
    filterIntegration: '🔌 Drivers e APIs',
    filterDesign: '📐 Guias de Instalação',
    filterDatasheets: '📄 Fichas Técnicas e CE',
    downloadPdf: 'Baixar PDF ↓',
    enDocBadge: 'Documento em inglês'
  },
  ar: {
    eyebrow: 'الوثائق والموارد التقنية',
    title: 'الأدلة وأوراق البيانات الفنية',
    desc: 'الوصول إلى الوثائق التقنية الرسمية، أدلة حلول Edge والخوادم المركزية، ومواصفات واجهات البرمجة.',
    allDocs: 'جميع الوثائق',
    filterEdge: '⚡ حلول Edge',
    filterCentralized: '🏢 الحلول المركزية',
    filterApps: '💻 التطبيقات',
    filterIntegration: '🔌 برامج التشغيل وواجهات API',
    filterDesign: '📐 أدلة التصميم والتركيب',
    filterDatasheets: '📄 أوراق البيانات والشهادات',
    downloadPdf: 'تحميل PDF ↓',
    enDocBadge: 'وثيقة بالإنجليزية'
  }
};

export function renderManualsAndDatasheetsPage() {
  const page = document.createElement('div');
  page.className = 'sed-page';
  const lang = getLocale() || 'en';
  const ui = manualsUi[lang] || manualsUi.en;

  const documents = [
    // 1. EDGE Solutions
    {
      title: 'AXIS ACAP Camera Plugin Manual v3.11',
      desc: 'Installation and configuration guide for AXIS Camera Application Platform (ACAP) edge acoustic analytics.',
      cat: 'edge',
      catLabel: lang === 'cs' ? 'Edge řešení' : 'EDGE Solutions',
      file: '/docs/edge/SED_ACAP_Manual_3.11_EN.pdf',
      size: '1.15 MB',
      version: 'v3.11'
    },
    {
      title: 'Hikvision HEOP Camera Plugin Manual v3.11',
      desc: 'Installation and configuration manual for Hikvision Embedded Open Platform (HEOP 2.0 / G5, G7, H13 chips) edge integration.',
      cat: 'edge',
      catLabel: lang === 'cs' ? 'Edge řešení' : 'EDGE Solutions',
      file: '/docs/edge/SED_HEOP_Manual_3.11_EN.pdf',
      size: '0.74 MB',
      version: 'v3.11'
    },
    {
      title: 'Hanwha Vision Wisenet Open Platform Integration Specs',
      desc: 'Edge deployment guide and audio analytics architecture for Hanwha Wisenet 9 and compatible camera SoC platforms.',
      cat: 'edge',
      catLabel: lang === 'cs' ? 'Edge řešení' : 'EDGE Solutions',
      file: '/docs/edge/SED_ACAP_Manual_3.11_EN.pdf',
      size: '1.15 MB',
      version: 'v3.11'
    },
    {
      title: 'SED Sound Event Detector Product Brochure',
      desc: 'Comprehensive overview of 5G acoustic sensors, NPU AI models, and real-time VMS auto-tracking capabilities.',
      cat: 'edge',
      catLabel: lang === 'cs' ? 'Edge řešení' : 'EDGE Solutions',
      file: '/docs/edge/SED Product Brochure_5G.pdf',
      size: '4.49 MB',
      version: 'Product Brochure'
    },

    // 2. Centralized Solutions
    {
      title: 'SED Target Server Manual v4.3',
      desc: 'Centralized server architecture, multi-node clustering, and target event routing specification.',
      cat: 'centralized',
      catLabel: lang === 'cs' ? 'Centralizovaná řešení' : 'Centralized Solutions',
      file: '/docs/centralized/SED_Target_4.3_EN.pdf',
      size: '0.37 MB',
      version: 'v4.3'
    },
    {
      title: 'SED Centralized Server System Manual v4.0',
      desc: 'Complete administrative manual for deploying centralized SED sound analytics servers.',
      cat: 'centralized',
      catLabel: lang === 'cs' ? 'Centralizovaná řešení' : 'Centralized Solutions',
      file: '/docs/centralized/SED_Server_4.0_EN.pdf',
      size: '4.38 MB',
      version: 'v4.0'
    },

    // 3. Software Applications
    {
      title: 'SED Operator Application User Manual v4.2',
      desc: 'User guide for security operators monitoring live acoustic events, map locations, and PTZ camera feeds.',
      cat: 'applications',
      catLabel: lang === 'cs' ? 'Aplikace' : 'Software Applications',
      file: '/docs/applications/SEDOperator_Manual_v4.2_EN.pdf',
      size: '2.03 MB',
      version: 'v4.2'
    },
    {
      title: 'SED Integrator Configuration Tool Manual v4.2',
      desc: 'Configuration and setup guide for system integrators deploying multi-sensor SED networks.',
      cat: 'applications',
      catLabel: lang === 'cs' ? 'Aplikace' : 'Software Applications',
      file: '/docs/applications/SEDIntegrator_Manual_v4.2_EN.pdf',
      size: '1.42 MB',
      version: 'v4.2'
    },
    {
      title: 'SED Device Settings Guide v3.11',
      desc: 'Sensor threshold calibration, gain controls, and acoustic frequency tuning manual.',
      cat: 'applications',
      catLabel: lang === 'cs' ? 'Aplikace' : 'Software Applications',
      file: '/docs/applications/SED_Device_Settings_v3.11_EN.pdf',
      size: '0.29 MB',
      version: 'v3.11'
    },

    // 4. Integration Drivers & APIs
    {
      title: 'SED REST API Driver Specifications v4.2',
      desc: 'HTTP REST API protocol documentation, Basic Auth endpoints, JSON payload schemas, and event polling guide.',
      cat: 'integration',
      catLabel: lang === 'cs' ? 'Ovladače a API' : 'Integration & APIs',
      file: '/docs/integration/SED_REST_Driver_4.2_EN.pdf',
      size: '0.41 MB',
      version: 'v4.2'
    },
    {
      title: 'SED ONVIF Metadata & Analytics Driver v3.11.1',
      desc: 'ONVIF Profile S and Profile M XML metadata driver specifications for Milestone XProtect, Genetec & Avigilon.',
      cat: 'integration',
      catLabel: lang === 'cs' ? 'Ovladače a API' : 'Integration & APIs',
      file: '/docs/integration/SED_ONVIF_Driver_3.11.1_EN.pdf',
      size: '1.93 MB',
      version: 'v3.11.1'
    },
    {
      title: 'SED SAPIENT Protocol Driver v4.1 (NATO STANREC 4869 / BSI Flex 335)',
      desc: 'NATO STANREC 4869 / BSI Flex 335 v2.0 SAPIENT standard integration driver for defense, autonomous sensor modules (ASM), and C2 multi-sensor fusion.',
      cat: 'integration',
      catLabel: lang === 'cs' ? 'Ovladače a API' : 'Integration & APIs',
      file: '/docs/integration/SED_SAPIENT_Driver_4.1_EN.pdf',
      size: '0.40 MB',
      version: 'v4.1'
    },

    // 5. Design Guides & Installation
    {
      title: 'Technology SED - Design Handbook v1.2',
      desc: 'Acoustic coverage calculations, sensor spacing guidelines, and architectural design principles for smart cities.',
      cat: 'design_guides',
      catLabel: lang === 'cs' ? 'Instalační příručky' : 'Design & Installation',
      file: '/docs/design_guides/Technology SED - Design handbook v1.2_EN.pdf',
      size: '2.20 MB',
      version: 'v1.2'
    },
    {
      title: 'System Installation & Configuration Manual v3.11',
      desc: 'Hardware mounting, PoE cabling, microphone positioning, and weatherproofing handbook.',
      cat: 'design_guides',
      catLabel: lang === 'cs' ? 'Instalační příručky' : 'Design & Installation',
      file: '/docs/design_guides/Instruction manual_v3.11_EN.pdf',
      size: '2.11 MB',
      version: 'v3.11'
    },
    {
      title: 'SED Hardware Installation Kit Guide v1.0',
      desc: 'Physical mounting brackets, pole clamps, and junction box installation specifications.',
      cat: 'design_guides',
      catLabel: lang === 'cs' ? 'Instalační příručky' : 'Design & Installation',
      file: '/docs/design_guides/SED_installation_kit_v1.0.pdf',
      size: '0.33 MB',
      version: 'v1.0'
    },

    // 6. Datasheets & Compliance
    {
      title: 'Sound Event Detector Product Datasheet v1.6',
      desc: 'Complete hardware specifications, NPU processing power, power consumption, and environmental ratings.',
      cat: 'datasheets',
      catLabel: lang === 'cs' ? 'Technické listy' : 'Datasheets & Compliance',
      file: '/docs/datasheets/SED Data Sheet v1.6_EN.pdf',
      size: '0.42 MB',
      version: 'v1.6'
    },
    {
      title: 'Acoustic Drone Detection Technical Datasheet',
      desc: '360° drone rotor blade frequency detection ranges, acoustics algorithms, and false alarm suppression.',
      cat: 'datasheets',
      catLabel: lang === 'cs' ? 'Technické listy' : 'Datasheets & Compliance',
      file: '/docs/datasheets/Technical Drone Datascheet.pdf',
      size: '1.22 MB',
      version: 'Technical Spec'
    },
    {
      title: 'Acoustic Verification & Field Testing Datasheet v2.0',
      desc: 'Empirical ballistic gunshot sound testing data, decibel thresholds, and environmental noise robustness results.',
      cat: 'datasheets',
      catLabel: lang === 'cs' ? 'Technické listy' : 'Datasheets & Compliance',
      file: '/docs/datasheets/SED_Data Sheet_Testing_EN_v2.pdf',
      size: '4.49 MB',
      version: 'v2.0'
    },
    {
      title: 'EU & FCC Declaration of Conformity (Official Signed)',
      desc: 'Official CE and FCC Part 15 Subpart B / ICES-003 Declaration of Conformity for Sound Event Detector indoor and outdoor units, certified under Directives 2014/35/EU, 2014/30/EU, 2001/95/EC, 2011/65/EU and EN 60335-1 / EN 61000 standards.',
      cat: 'datasheets',
      catLabel: lang === 'cs' ? 'Certifikace a CE' : 'Datasheets & Compliance',
      file: '/docs/datasheets/SED_EU_FCC_Declaration_of_Conformity.pdf',
      size: '0.15 MB',
      version: 'EU & FCC Certified'
    },
    {
      title: 'GDPR & Privacy Compliance Statement',
      desc: 'GDPR compliance certification and legal opinion confirming zero-continuous-audio-recording architecture.',
      cat: 'datasheets',
      catLabel: lang === 'cs' ? 'GDPR Certifikace' : 'Datasheets & Compliance',
      file: '/docs/datasheets/SED_GDPR_statement_EN.pdf',
      size: '0.79 MB',
      version: 'GDPR Certified'
    },
    {
      title: 'Official GDPR Law Study & Legal Opinion (GÖRGES & PARTNERS)',
      desc: 'Independent legal assessment by JUDr. Marek Görges jr. confirming SED system architecture full compliance with Regulation (EU) 2016/679 (GDPR).',
      cat: 'datasheets',
      catLabel: lang === 'cs' ? 'Právní posudek GDPR' : 'Datasheets & Compliance',
      file: '/docs/datasheets/SED_GDPR_Legal_Opinion_Study.pdf',
      size: '0.10 MB',
      version: 'Legal Opinion'
    }
  ];

  let currentCategory = 'all';

  page.innerHTML = `
    <section class="sed-section" style="background: linear-gradient(165deg, var(--blue-light) 0%, #ffffff 70%); padding: 4.5rem 0 3rem;">
      <div class="sed-container">
        <div class="sed-section-header" style="max-width: 850px; margin-bottom: 2rem;">
          <div class="sed-eyebrow">${ui.eyebrow}</div>
          <h1 class="sed-page-hero-title">${ui.title}</h1>
          <p class="sed-section-desc">
            ${ui.desc}
          </p>
        </div>

        <!-- Filter Tabs (All 7 Categories Localized) -->
        <div style="display: flex; gap: 10px; flex-wrap: wrap; margin-top: 2rem;" id="sed-docs-filter-bar">
          <button class="sed-btn active-tab" data-cat="all" style="padding: 8px 16px; border-radius: 20px; font-size: 0.85rem; font-weight: 700; border: 1px solid var(--blue-primary); cursor: pointer; transition: all 0.2s ease;">${ui.allDocs} (${documents.length})</button>
          <button class="sed-btn inactive-tab" data-cat="edge" style="padding: 8px 16px; border-radius: 20px; font-size: 0.85rem; font-weight: 700; border: 1px solid var(--slate-line); cursor: pointer; transition: all 0.2s ease;">${ui.filterEdge}</button>
          <button class="sed-btn inactive-tab" data-cat="centralized" style="padding: 8px 16px; border-radius: 20px; font-size: 0.85rem; font-weight: 700; border: 1px solid var(--slate-line); cursor: pointer; transition: all 0.2s ease;">${ui.filterCentralized}</button>
          <button class="sed-btn inactive-tab" data-cat="applications" style="padding: 8px 16px; border-radius: 20px; font-size: 0.85rem; font-weight: 700; border: 1px solid var(--slate-line); cursor: pointer; transition: all 0.2s ease;">${ui.filterApps}</button>
          <button class="sed-btn inactive-tab" data-cat="integration" style="padding: 8px 16px; border-radius: 20px; font-size: 0.85rem; font-weight: 700; border: 1px solid var(--slate-line); cursor: pointer; transition: all 0.2s ease;">${ui.filterIntegration}</button>
          <button class="sed-btn inactive-tab" data-cat="design_guides" style="padding: 8px 16px; border-radius: 20px; font-size: 0.85rem; font-weight: 700; border: 1px solid var(--slate-line); cursor: pointer; transition: all 0.2s ease;">${ui.filterDesign}</button>
          <button class="sed-btn inactive-tab" data-cat="datasheets" style="padding: 8px 16px; border-radius: 20px; font-size: 0.85rem; font-weight: 700; border: 1px solid var(--slate-line); cursor: pointer; transition: all 0.2s ease;">${ui.filterDatasheets}</button>
        </div>
      </div>
    </section>

    <section class="sed-section" style="background-color: #ffffff; padding: 2rem 0 5rem;">
      <div class="sed-container">
        <div class="sed-grid-3" id="sed-docs-grid" style="gap: 1.75rem;">
        </div>
      </div>
    </section>
  `;

  function renderGrid() {
    const gridEl = page.querySelector('#sed-docs-grid');
    if (!gridEl) return;

    const filtered = currentCategory === 'all' 
      ? documents 
      : documents.filter(d => d.cat === currentCategory);

    gridEl.innerHTML = filtered.map(doc => `
      <div class="sed-card" style="display: flex; flex-direction: column; justify-content: space-between; padding: 1.75rem; border-radius: 14px; border: 1.5px solid var(--slate-line); transition: all 0.3s ease; box-shadow: 0 4px 15px rgba(0,0,0,0.03);" onmouseover="this.style.transform='translateY(-3px)'; this.style.boxShadow='0 10px 25px rgba(0,0,0,0.08)';" onmouseout="this.style.transform='none'; this.style.boxShadow='0 4px 15px rgba(0,0,0,0.03)';">
        <div>
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.85rem; flex-wrap: wrap; gap: 0.5rem;">
            <span style="background: var(--blue-ultra-light); color: var(--blue-primary); font-size: 0.72rem; font-weight: 800; padding: 3px 9px; border-radius: 6px; text-transform: uppercase;">${doc.catLabel}</span>
            <div style="display: flex; gap: 6px; align-items: center;">
              ${doc.file.endsWith('_EN.pdf') || doc.file.endsWith('.pdf') ? `
                <span style="font-size: 0.7rem; font-weight: 700; color: #475569; background: #f1f5f9; padding: 2px 6px; border-radius: 4px;">EN</span>
              ` : ''}
              <span style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted);">${doc.version}</span>
            </div>
          </div>
          <h3 style="font-size: 1.12rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.6rem; line-height: 1.35;">📄 ${doc.title}</h3>
          <p style="font-size: 0.88rem; color: var(--text-dark); line-height: 1.55; margin-bottom: 1.25rem;">${doc.desc}</p>
        </div>
        <div style="display: flex; justify-content: space-between; align-items: center; padding-top: 1rem; border-top: 1px solid var(--slate-line);">
          <span style="font-size: 0.76rem; font-weight: 600; color: var(--text-muted);">PDF · ${doc.size}</span>
          <a href="${doc.file}" target="_blank" download class="sed-btn sed-btn-primary" style="padding: 7px 16px; font-size: 0.82rem; font-weight: 700; text-decoration: none; border-radius: 8px; display: inline-flex; align-items: center; gap: 6px;">
            <span>${ui.downloadPdf}</span>
          </a>
        </div>
      </div>
    `).join('');
  }

  // Filter bar event handlers
  const filterBtns = page.querySelectorAll('#sed-docs-filter-bar button');
  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      filterBtns.forEach(b => {
        b.style.background = 'transparent';
        b.style.color = 'var(--text-dark)';
        b.style.borderColor = 'var(--slate-line)';
      });
      btn.style.background = 'var(--blue-primary)';
      btn.style.color = '#ffffff';
      btn.style.borderColor = 'var(--blue-primary)';

      currentCategory = btn.getAttribute('data-cat');
      renderGrid();
    });
  });

  // Highlight initial active tab
  const activeBtn = page.querySelector('[data-cat="all"]');
  if (activeBtn) {
    activeBtn.style.background = 'var(--blue-primary)';
    activeBtn.style.color = '#ffffff';
    activeBtn.style.borderColor = 'var(--blue-primary)';
  }

  renderGrid();
  return page;
}
